import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  fetchAccount,
  openBillingPortal,
  startCheckout,
  type BillingAccount,
} from '../services/billing';
import { PageHeader } from '../components/PageHeader';

const yuan = (cents: number) => `¥${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const FREE_FEATURES = [
  '梅花、奇门、八字排盘不限次',
  '注册赠送 10 次 AI 解读',
  '历史记录与追问云端保存',
];

const BASIC_FEATURES = [
  '每月 200 次 AI 解读',
  '追问不另计额度',
  '优先体验新模型与解读模板',
  '随时取消，权益保留至周期结束',
];

const Check = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m4.5 12.5 5 5 10-11" />
  </svg>
);

export const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      setAccount(await fetchAccount(user));
    } catch {
      /* 账户信息拿不到时退回默认套餐展示，不打断浏览 */
    }
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const subscribe = async () => {
    if (!user || busy) return;
    setBusy(true);
    setError(null);
    try {
      const url = await startCheckout(user);
      window.location.href = url;
    } catch {
      setError('暂时无法发起订阅，请稍后再试。');
      setBusy(false);
    }
  };

  const manage = async () => {
    if (!user || busy) return;
    setBusy(true);
    setError(null);
    try {
      const url = await openBillingPortal(user);
      window.location.href = url;
    } catch {
      setError('暂时无法打开账单门户，请稍后再试。');
      setBusy(false);
    }
  };

  const isBasic = account?.plan === 'basic';
  const basicPlan = account?.plans.find((p) => p.id === 'basic');
  const freePlan = account?.plans.find((p) => p.id === 'free');
  const basicPrice = basicPlan?.priceCents ?? 2800;
  const freeFeatures = freePlan?.features?.length ? freePlan.features : FREE_FEATURES;
  const basicFeatures = basicPlan?.features?.length ? basicPlan.features : BASIC_FEATURES;

  return (
    <div className="page">
      <PageHeader
        eyebrow="订阅"
        title="订阅计划"
        subtitle="排盘永久免费；AI 解读按次计费。需要更多解读时，再按月订阅。"
        actions={<button className="text-btn" onClick={() => navigate('/app/settings')}>返回</button>}
      />

      {account && (
        <div className="mei-credit-bar">
          <span>
            当前套餐 <b>{account.planName}</b>
            {account.planStatus === 'active' && isBasic ? ' · 已生效' : ''}
          </span>
          <span>剩余 <b>{account.balance}</b> 次</span>
        </div>
      )}

      {error && <div className="mei-error">{error}</div>}

      <div className="pricing-grid">
        <section className="price-card">
          <div className="price-head">
            <div>
              <div className="price-name">免费</div>
              <div className="price-desc">注册即用，先体验完整排盘</div>
            </div>
            <div className="price-amount">
              <b>{yuan(0)}</b>
              <span>/ 永久</span>
            </div>
          </div>
          <ul className="price-features">
            {freeFeatures.map((f) => (
              <li key={f}><span className="price-check">{<Check />}</span>{f}</li>
            ))}
          </ul>
          <div className="price-action">
            <span className="price-current">{isBasic ? '已升级基础版' : '当前套餐'}</span>
          </div>
        </section>

        <section className="price-card is-featured">
          <div className="price-ribbon">推荐</div>
          <div className="price-head">
            <div>
              <div className="price-name">基础版</div>
              <div className="price-desc">适合持续问事与复盘</div>
            </div>
            <div className="price-amount">
              <b>{yuan(basicPrice)}</b>
              <span>/ 月</span>
            </div>
          </div>
          <ul className="price-features">
            {basicFeatures.map((f) => (
              <li key={f}><span className="price-check">{<Check />}</span>{f}</li>
            ))}
          </ul>
          <div className="price-action">
            {isBasic ? (
              <button className="mei-ghost-btn" onClick={manage} disabled={busy}>
                {busy ? '打开中…' : '管理订阅 / 取消'}
              </button>
            ) : (
              <button className="mei-primary-btn" onClick={subscribe} disabled={busy}>
                {busy ? '跳转结算…' : '订阅基础版'}
              </button>
            )}
          </div>
        </section>
      </div>

      <p className="price-foot">
        由 Stripe 安全收款，可随时在账单门户取消。订阅期间取消，权益保留至本周期结束。
      </p>
      <p className="price-disclaimer">排盘与 AI 解读仅供传统文化研究参考，玄学内容请理性看待。</p>
    </div>
  );
};
